import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NativeCheckbox } from '@/components/ui/NativeCheckbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/App';
import { COUNTRIES } from '@/data/countries';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volley-club/artifacts/dg49xs0l_Black%20Decorative%20Shield%20Volleyball%20Club%20Logo%20%28Sweatshirts%29%20%28T-Shirts%29.png";

const POSITIONS = [
  'Libero',
  'Outside Hitter',
  'Middle Blocker',
  'Opposite Hitter',
  'Setter'
];

const LEVELS = [
  '6th class',
  '5th class',
  '4th class',
  '3rd class',
  '2nd class',
  '1st class',
  'Promotion class',
  '2nd division',
  '1st division',
  'Other'
];

const GENDER_OPTIONS = [
  'Male',
  'Female',
  'Non-Binary/Other',
  'Prefer not to say'
];

export default function RegisterPage({ onRegister }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    preferred_name: '',
    date_of_birth: '',
    phone: '',
    gender: '',
    volleyball_experience: '',
    position_1st: '',
    position_2nd: '',
    position_3rd: '',
    current_level: '',
    current_level_other: '',
    highest_level: '',
    highest_level_other: '',
    where_hear_about_us: '',
    club_experience: false,
    club_names: '',
    current_club: '',
    previous_clubs: '',
    country_of_origin: '',
    current_country: '',
    current_city: '',
    gdpr_consent: false,
    terms_consent: false
  });

  const showLevelFields = formData.volleyball_experience && formData.volleyball_experience !== 'Beginner';
  
  // Validation for level fields
  const areLevelFieldsRequired = showLevelFields;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate profile photo (MANDATORY)
    if (!profilePhoto) {
      toast.error('Profile photo is required. Please upload your photo to continue.');
      return;
    }
    
    // Validate password confirmation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate experience is selected
    if (!formData.volleyball_experience) {
      toast.error('Please select your volleyball experience level');
      return;
    }

    // Validate gender
    if (!formData.gender) {
      toast.error('Please select your gender');
      return;
    }

    // Validate first position (mandatory)
    if (!formData.position_1st) {
      toast.error('Please select your 1st favorite position');
      return;
    }

    // Validate that 1st, 2nd and 3rd preferred positions are all different
    const chosenPositions = [formData.position_1st, formData.position_2nd, formData.position_3rd].filter(Boolean);
    if (new Set(chosenPositions).size !== chosenPositions.length) {
      toast.error('Please choose a different position for each preference (1st, 2nd, 3rd).');
      return;
    }

    // Validate where heard about us
    if (!formData.where_hear_about_us || formData.where_hear_about_us.trim() === '') {
      toast.error('Please tell us where you heard about us');
      return;
    }

    // Validate GDPR consent
    if (!formData.gdpr_consent) {
      toast.error('Please agree to the GDPR consent to proceed');
      return;
    }

    // Validate Terms consent
    if (!formData.terms_consent) {
      toast.error('Please agree to the Terms and Conditions and Privacy Policy');
      return;
    }

    // Validate level fields if experience is not Beginner
    if (areLevelFieldsRequired) {
      if (!formData.current_level) {
        toast.error('Please select your current level');
        return;
      }
      if (formData.current_level === 'Other' && !formData.current_level_other) {
        toast.error('Please specify your current level');
        return;
      }
      if (!formData.highest_level) {
        toast.error('Please select your highest level ever played');
        return;
      }
      if (formData.highest_level === 'Other' && !formData.highest_level_other) {
        toast.error('Please specify your highest level');
        return;
      }
    }
    
    setLoading(true);

    try {
      // Remove confirm_password and terms_consent before sending to API
      const { confirm_password, terms_consent, ...registrationData } = formData;
      const response = await api.post('/auth/register', registrationData);
      
      // Upload profile photo if provided
      if (profilePhoto) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', profilePhoto);
          await api.post('/users/upload-photo', photoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${response.data.access_token}`
            }
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          // Don't fail registration if photo upload fails
        }
      }
      
      toast.success('Registration successful! Your account is pending approval.');
      onRegister(response.data.access_token, response.data.user);
      navigate('/pending-approval');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-white to-blue-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="NLV Logo" className="w-20 h-20 object-contain" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Join NLV</h1>
            <p className="text-gray-600">Create your account and start playing!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    data-testid="first-name-input"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    data-testid="last-name-input"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="preferred_name">Preferred Name (Optional)</Label>
                <Input
                  id="preferred_name"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({...formData, preferred_name: e.target.value})}
                  className="mt-2"
                  placeholder="How would you like to be called on game lists?"
                />
                <p className="text-sm text-gray-500 mt-1">This name will appear on game registration lists. If left blank, your full name will be used.</p>
              </div>

              <div className="mb-6">
                <Label htmlFor="email">Email Address (Your Username) *</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-2"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-gray-500 mt-1">This will be your username for logging in</p>
              </div>

              <div className="mb-6">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="mb-6">
                <Label htmlFor="confirm_password">Confirm Password *</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  required
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    data-testid="dob-input"
                    type="date"
                    required
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2"
                    placeholder="+31..."
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="gender">Gender * <span className="text-sm font-normal text-gray-500">(Helps us balance teams for mix or single gender games)</span></Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData({...formData, gender: value})}
                  required
                >
                  <SelectTrigger className="mt-2" id="gender">
                    <SelectValue placeholder="Select your gender" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="profile_photo">Profile Photo * <span className="text-sm font-normal text-gray-500">(Required - Max 5MB)</span></Label>
                <div className="mt-2 mb-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <p className="text-sm text-blue-900 font-semibold mb-1">📸 Photo guidelines</p>
                  <ul className="text-xs text-blue-800 list-disc pl-5 space-y-0.5">
                    <li>Use a clear, well-lit <strong>face photo</strong> so teammates and admins can recognize you.</li>
                    <li>Your face should be fully visible — no sunglasses, hats, masks, or heavy filters.</li>
                    <li>Centered headshot on a plain background works best.</li>
                    <li>No group photos, avatars, logos, memes, pets, or landscape pictures.</li>
                    <li>Formats: JPG, PNG, or WebP. Max 5 MB.</li>
                  </ul>
                </div>
                <div className="mt-2">
                  <Input
                    id="profile_photo"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('File size must be less than 5MB');
                          e.target.value = '';
                          return;
                        }
                        setProfilePhoto(file);
                        setPhotoPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="mt-2"
                  />
                  {photoPreview && (
                    <div className="mt-3 flex items-center gap-4">
                      <img src={photoPreview} alt="Profile preview" className="w-20 h-20 rounded-full object-cover border-2 border-orange-500" />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setProfilePhoto(null);
                          setPhotoPreview(null);
                          document.getElementById('profile_photo').value = '';
                        }}
                      >
                        Remove Photo
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Please follow the photo guidelines above — unclear photos may delay approval.</p>
              </div>
            </div>

            {/* Volleyball Experience Section */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Volleyball Experience</h2>
              
              <div className="mb-6">
                <Label htmlFor="volleyball_experience">Volleyball Experience *</Label>
                <Select
                  value={formData.volleyball_experience}
                  onValueChange={(value) => setFormData({...formData, volleyball_experience: value})}
                  required
                >
                  <SelectTrigger data-testid="experience-select" className="mt-2" id="volleyball_experience">
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate (1-3 years)">Intermediate (1-3 years)</SelectItem>
                    <SelectItem value="Upper Intermediate (4-6 years)">Upper Intermediate (4-6 years)</SelectItem>
                    <SelectItem value="Advanced (7+ years)">Advanced (7+ years)</SelectItem>
                    <SelectItem value="Professional (played in local or national leagues)">Professional (played in local or national leagues)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Position Preferences */}
              <div className="mb-6">
                <Label htmlFor="position_1st">1st Favorite Position *</Label>
                <Select
                  value={formData.position_1st}
                  onValueChange={(value) => setFormData({...formData, position_1st: value})}
                  required
                >
                  <SelectTrigger className="mt-2" id="position_1st">
                    <SelectValue placeholder="Select your 1st favorite position" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_1st && (pos === formData.position_2nd || pos === formData.position_3rd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="position_2nd">2nd Favorite Position (Optional)</Label>
                <Select
                  value={formData.position_2nd}
                  onValueChange={(value) => setFormData({...formData, position_2nd: value})}
                >
                  <SelectTrigger className="mt-2" id="position_2nd">
                    <SelectValue placeholder="Select your 2nd favorite position (or leave blank)" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_2nd && (pos === formData.position_1st || pos === formData.position_3rd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="position_3rd">3rd Favorite Position (Optional)</Label>
                <Select
                  value={formData.position_3rd}
                  onValueChange={(value) => setFormData({...formData, position_3rd: value})}
                >
                  <SelectTrigger className="mt-2" id="position_3rd">
                    <SelectValue placeholder="Select your 3rd favorite position (or leave blank)" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_3rd && (pos === formData.position_1st || pos === formData.position_2nd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label>Have you ever played in a volleyball club? *</Label>
                <div className="flex gap-4 mt-2">
                  <NativeCheckbox
                    id="club-yes"
                    checked={formData.club_experience === true}
                    onChange={() => setFormData({...formData, club_experience: true})}
                    label="Yes"
                  />
                  <NativeCheckbox
                    id="club-no"
                    checked={formData.club_experience === false}
                    onChange={() => setFormData({...formData, club_experience: false})}
                    label="No"
                  />
                </div>
              </div>

              {formData.club_experience && (
                <div className="mb-6 space-y-4">
                  <div>
                    <Label htmlFor="current_club">Current Club</Label>
                    <Input
                      id="current_club"
                      value={formData.current_club}
                      onChange={(e) => setFormData({...formData, current_club: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., Amsterdam Volleyball Club"
                    />
                  </div>
                  <div>
                    <Label htmlFor="previous_clubs">Previous Clubs (optional)</Label>
                    <Input
                      id="previous_clubs"
                      value={formData.previous_clubs}
                      onChange={(e) => setFormData({...formData, previous_clubs: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., Rotterdam VC, Utrecht Smash — separate with commas"
                    />
                  </div>
                </div>
              )}

              {/* Conditional Level Fields */}
              {showLevelFields && (
                <>
                  <div className="mb-6">
                    <Label htmlFor="current_level">What is your current level? *</Label>
                    <Select
                      value={formData.current_level}
                      onValueChange={(value) => setFormData({...formData, current_level: value})}
                      required
                    >
                      <SelectTrigger className="mt-2" id="current_level">
                        <SelectValue placeholder="Select your current level" />
                      </SelectTrigger>
                      <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                        {LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.current_level === 'Other' && (
                    <div className="mb-6">
                      <Label htmlFor="current_level_other">Please specify your current level *</Label>
                      <Input
                        id="current_level_other"
                        required
                        value={formData.current_level_other}
                        onChange={(e) => setFormData({...formData, current_level_other: e.target.value})}
                        className="mt-2"
                        placeholder="e.g., Regional league"
                      />
                    </div>
                  )}

                  <div className="mb-6">
                    <Label htmlFor="highest_level">What was the highest level you have ever played? *</Label>
                    <Select
                      value={formData.highest_level}
                      onValueChange={(value) => setFormData({...formData, highest_level: value})}
                      required
                    >
                      <SelectTrigger className="mt-2" id="highest_level">
                        <SelectValue placeholder="Select your highest level" />
                      </SelectTrigger>
                      <SelectContent className="z-50 max-h-[300px]" position="popper" sideOffset={4}>
                        {LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.highest_level === 'Other' && (
                    <div className="mb-6">
                      <Label htmlFor="highest_level_other">Please specify your highest level *</Label>
                      <Input
                        id="highest_level_other"
                        required
                        value={formData.highest_level_other}
                        onChange={(e) => setFormData({...formData, highest_level_other: e.target.value})}
                        className="mt-2"
                        placeholder="e.g., National team"
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Location Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Location Information</h2>
              
              <div className="mb-6">
                <Label htmlFor="country_of_origin">Where are you from? (Country of Origin) *</Label>
                <Select
                  value={formData.country_of_origin}
                  onValueChange={(value) => setFormData({...formData, country_of_origin: value})}
                >
                  <SelectTrigger className="mt-2" id="country_of_origin">
                    <SelectValue placeholder="Select your country of origin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="current_country">Which country do you currently live in? *</Label>
                <Select
                  value={formData.current_country}
                  onValueChange={(value) => setFormData({...formData, current_country: value})}
                >
                  <SelectTrigger className="mt-2" id="current_country">
                    <SelectValue placeholder="Select your current country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="current_city">Which city do you currently live in? *</Label>
                <Input
                  id="current_city"
                  required
                  value={formData.current_city}
                  onChange={(e) => setFormData({...formData, current_city: e.target.value})}
                  className="mt-2"
                  placeholder="e.g., Amsterdam"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Additional Information</h2>
              
              <div className="mb-6">
                <Label htmlFor="where_hear_about_us">Where did you hear about us? *</Label>
                <Textarea
                  id="where_hear_about_us"
                  required
                  value={formData.where_hear_about_us}
                  onChange={(e) => setFormData({...formData, where_hear_about_us: e.target.value})}
                  className="mt-2"
                  placeholder="e.g., Through a friend — John Doe, Instagram, Facebook, Google search..."
                  rows={3}
                />
                <p className="text-xs text-gray-600 mt-1.5">
                  👉 If you heard about us <strong>through a friend</strong>, please include their <strong>name(s)</strong> so we can thank them.
                </p>
                {/^\s*friend/i.test(formData.where_hear_about_us) && !/(\b[A-Z][a-zA-ZçğıöşüÇĞİÖŞÜ]+\b)/.test(formData.where_hear_about_us) && (
                  <p className="text-xs text-orange-600 mt-1" data-testid="friend-name-hint">
                    ⚠️ Looks like you were referred by a friend — don't forget to mention their name!
                  </p>
                )}
              </div>
            </div>

            {/* GDPR & Legal Consent */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-3">
              <NativeCheckbox
                id="gdpr-consent"
                checked={formData.gdpr_consent}
                onChange={(checked) => setFormData({...formData, gdpr_consent: checked})}
                label={
                  <span className="text-sm">
                    I agree to the processing of my data for organizing volleyball games and events. <span className="text-red-500">*</span>
                  </span>
                }
              />
              <NativeCheckbox
                id="terms-consent"
                checked={formData.terms_consent}
                onChange={(checked) => setFormData({...formData, terms_consent: checked})}
                label={
                  <span className="text-sm">
                    I have read and agree to the{' '}
                    <a href="/terms" target="_blank" className="text-orange-600 hover:text-orange-700 underline font-semibold">
                      Terms and Conditions
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" className="text-orange-600 hover:text-orange-700 underline font-semibold">
                      Privacy Policy
                    </a>
                    . <span className="text-red-500">*</span>
                  </span>
                }
              />
            </div>

            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-6 text-lg rounded-full"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-orange-600 font-semibold hover:text-orange-700"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
